import * as React from 'react';

import { getRuntimeSurfaceContrastColors, resolveElementBackgroundColor } from '@/lib/contrastUtils';

type UseSurfaceContrastVariablesOptions = {
  preferredText?: string;
  minimumContrast?: number;
  disabled?: boolean;
};

const OBSERVED_ATTRIBUTES = ['class', 'style', 'data-state', 'data-active', 'aria-selected'];

export function useSurfaceContrastVariables<T extends HTMLElement>({
  preferredText,
  minimumContrast,
  disabled = false,
}: UseSurfaceContrastVariablesOptions = {}) {
  const elementRef = React.useRef<T | null>(null);
  const frameRef = React.useRef<number | null>(null);

  const clearColors = React.useCallback(() => {
    const element = elementRef.current;
    if (!element) return;

    element.style.removeProperty('--surface-fg');
    element.style.removeProperty('--surface-muted-fg');
  }, []);

  const applyColors = React.useCallback(() => {
    const element = elementRef.current;
    if (!element) return;

    if (disabled) {
      clearColors();
      return;
    }

    const backgroundColor = resolveElementBackgroundColor(element);
    const colors = getRuntimeSurfaceContrastColors(
      backgroundColor,
      preferredText,
      minimumContrast,
    );

    element.style.setProperty('--surface-fg', colors.foreground);
    element.style.setProperty('--surface-muted-fg', colors.mutedForeground);
  }, [clearColors, disabled, minimumContrast, preferredText]);

  const scheduleRecompute = React.useCallback(() => {
    if (typeof window === 'undefined') return;

    if (frameRef.current !== null) {
      window.cancelAnimationFrame(frameRef.current);
    }

    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = null;
      applyColors();
    });
  }, [applyColors]);

  const ref = React.useCallback((node: T | null) => {
    elementRef.current = node;

    if (node) {
      if (disabled) {
        node.style.removeProperty('--surface-fg');
        node.style.removeProperty('--surface-muted-fg');
        return;
      }

      scheduleRecompute();
    }
  }, [disabled, scheduleRecompute]);

  React.useLayoutEffect(() => {
    if (disabled) {
      clearColors();
      return;
    }

    scheduleRecompute();
  }, [clearColors, disabled, scheduleRecompute]);

  React.useEffect(() => {
    if (disabled) return;

    const element = elementRef.current;
    if (!element || typeof window === 'undefined') return;

    const handleInteraction = () => scheduleRecompute();

    const root = document.documentElement;
    const rootObserver = new MutationObserver(handleInteraction);
    const elementObserver = new MutationObserver(handleInteraction);

    rootObserver.observe(root, { attributes: true, attributeFilter: ['class', 'style'] });
    elementObserver.observe(element, { attributes: true, attributeFilter: OBSERVED_ATTRIBUTES });

    element.addEventListener('pointerenter', handleInteraction);
    element.addEventListener('pointerleave', handleInteraction);
    element.addEventListener('focus', handleInteraction);
    element.addEventListener('blur', handleInteraction);
    element.addEventListener('transitionend', handleInteraction);
    window.addEventListener('resize', handleInteraction);

    return () => {
      rootObserver.disconnect();
      elementObserver.disconnect();
      element.removeEventListener('pointerenter', handleInteraction);
      element.removeEventListener('pointerleave', handleInteraction);
      element.removeEventListener('focus', handleInteraction);
      element.removeEventListener('blur', handleInteraction);
      element.removeEventListener('transitionend', handleInteraction);
      window.removeEventListener('resize', handleInteraction);

      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [disabled, scheduleRecompute]);

  return {
    ref,
    recompute: scheduleRecompute,
  };
}
